import type { JobTypeName } from "@tradingdroid/frontends-types";
import cloneDeep from "lodash-es/cloneDeep";
import get from "lodash-es/get";
import isPlainObject from "lodash-es/isPlainObject";
import set from "lodash-es/set";

/**
 * The two exit policy keys the API defaults together: a new backtest, optimization or walk-forward
 * job that requests neither is stamped with the current policy when it is created.
 */
export const EXIT_POLICY_KEYS = ["exit_arbitration", "exit_semantics_version"] as const;

export type ExitPolicyKey = (typeof EXIT_POLICY_KEYS)[number];

/**
 * The policy the engine resolves a config to when it requests neither key: no arbitration, exit
 * semantics version 1. A copy of such a source pins it, because the API would otherwise stamp the
 * current policy on the copy.
 */
export const LEGACY_EXIT_POLICY: Readonly<Record<ExitPolicyKey, boolean | number>> = {
  exit_arbitration: false,
  exit_semantics_version: 1,
};

/**
 * The job types whose exit policy a copy settles, as its source and as its destination. Where a
 * copy writes a policy, it goes to a backtest's raw `exit_config`, an optimization's
 * `exit_param_overrides`, or a walk-forward's `optimization.exit_param_overrides`.
 */
export type ExitPolicyJobType = "backtest" | "optimization" | "walk_forward";

/**
 * How a source's two policy keys resolved, read together.
 *
 * - `skip`: a source outside `ExitPolicyJobType`. This helper leaves its config as stored
 *   instead of settling an exit policy.
 * - `unrequested`: neither key is requested. The copy pins the legacy policy.
 * - `explicit`: no key is searched, and `exit_semantics_version` states a single value, with or
 *   without the flag. A stated version decides the semantics by itself, so the copy keeps the
 *   source's values.
 * - `flag_only`: `exit_arbitration` states a single value and the version is not requested. The
 *   copy carries the flag and pins no version. `versionFloats` is true for `exit_arbitration:
 *   true`, which the engine resolves to its current version when the job runs.
 * - `searched`: at least one key is searched. A same-shape copy keeps the search, and a
 *   backtest-shaped copy leaves each searched key out.
 */
export type ExitPolicyClassification =
  | { kind: "skip" }
  | { kind: "unrequested" }
  | { kind: "explicit" }
  | { kind: "flag_only"; versionFloats: boolean }
  | { kind: "searched"; searchedKeys: ExitPolicyKey[] };

export interface ExitPolicyCloneResult<C> {
  /** A deep copy of the source config, with the exit policy settled for the destination. */
  config: C;
  policy: ExitPolicyClassification;
}

type ResolvedKey =
  | { state: "unrequested" }
  | { state: "fixed"; value: unknown }
  | { state: "searched" };

export const isRecord = (value: unknown): value is Record<string, unknown> => isPlainObject(value);

const asRecord = (value: unknown): Record<string, unknown> => (isRecord(value) ? value : {});

/**
 * A walk-forward job keeps its run config, and with it the exit policy, under `optimization`.
 * Backtest and optimization use the top-level config; types outside `ExitPolicyJobType` return null.
 */
export const runConfigBlock = (jobType: JobTypeName): string[] | null => {
  switch (jobType) {
    case "walk_forward":
      return ["optimization"];
    case "backtest":
    case "optimization":
      return [];
    default:
      return null;
  }
};

/**
 * Whether a policy value is the parameter registry's "decide for me" value rather than a request.
 * "auto" is one for either key. A numeric 0 is one for `exit_semantics_version` only, so
 * `exit_arbitration: 0` and both booleans are requests.
 */
const isPolicySentinel = (key: ExitPolicyKey, value: unknown): boolean =>
  value === "auto" || (key === "exit_semantics_version" && value === 0);

/**
 * Resolve one key from an override map and the raw exit_config beside it. A real override
 * decides: `{ fixed: value }` states that value, and any other entry, such as a range or a set of
 * categories, is a search. A missing override, or `{ fixed: <sentinel> }`, defers to a raw value
 * that is not itself a sentinel.
 */
const resolveKey = (
  key: ExitPolicyKey,
  overrides: Record<string, unknown>,
  raw: Record<string, unknown>,
): ResolvedKey => {
  const override = overrides[key];
  if (override !== undefined) {
    if (!isRecord(override) || !("fixed" in override)) return { state: "searched" };
    if (!isPolicySentinel(key, override.fixed)) return { state: "fixed", value: override.fixed };
  }
  const value = raw[key];
  if (value !== undefined && !isPolicySentinel(key, value)) return { state: "fixed", value };
  return { state: "unrequested" };
};

const resolveSourcePolicy = (
  sourceJobType: JobTypeName,
  sourceConfig: object,
  block: string[],
): Record<ExitPolicyKey, ResolvedKey> => {
  const raw = asRecord(get(sourceConfig, [...block, "exit_config"]));
  // A backtest states its policy in the raw exit_config alone.
  const overrides =
    sourceJobType === "backtest"
      ? {}
      : asRecord(get(sourceConfig, [...block, "exit_param_overrides"]));
  return {
    exit_arbitration: resolveKey("exit_arbitration", overrides, raw),
    exit_semantics_version: resolveKey("exit_semantics_version", overrides, raw),
  };
};

const classify = (resolved: Record<ExitPolicyKey, ResolvedKey>): ExitPolicyClassification => {
  const searchedKeys = EXIT_POLICY_KEYS.filter((key) => resolved[key].state === "searched");
  if (searchedKeys.length > 0) return { kind: "searched", searchedKeys };
  if (resolved.exit_semantics_version.state === "fixed") return { kind: "explicit" };
  const flag = resolved.exit_arbitration;
  if (flag.state === "fixed") return { kind: "flag_only", versionFloats: flag.value === true };
  return { kind: "unrequested" };
};

/** Classify a source job's exit policy without copying its config. */
export const classifyExitPolicy = (
  sourceJobType: JobTypeName,
  sourceConfig: object,
): ExitPolicyClassification => {
  const block = runConfigBlock(sourceJobType);
  return block === null
    ? { kind: "skip" }
    : classify(resolveSourcePolicy(sourceJobType, sourceConfig, block));
};

/**
 * Copy a source job's config for a new job, with its exit policy settled for the destination.
 *
 * A source outside `ExitPolicyJobType` returns a plain copy classified `skip`. For handled
 * sources the destination defaults to the source's own shape. A backtest destination is also
 * accepted from an optimization or walk-forward source; any other change of shape throws.
 *
 * - Same shape: requested values stay where the source stated them. A source that requests
 *   neither key gets the legacy policy pinned in the destination's container, as `{ fixed }`
 *   entries in an override map.
 * - Backtest destination from an override source: the copy's top-level `exit_config` starts from
 *   the source's raw `exit_config`. Each key with a single value is written into it, and each
 *   searched or unrequested key is removed, including a raw value an override shadowed. A source
 *   that requests neither key then gets the legacy policy pinned.
 *
 * The source config is not changed.
 */
export const cloneExitPolicy = <C extends object>(
  sourceJobType: JobTypeName,
  sourceConfig: C,
  destination?: ExitPolicyJobType,
): ExitPolicyCloneResult<C> => {
  const config = cloneDeep(sourceConfig);
  const block = runConfigBlock(sourceJobType);
  if (block === null) return { config, policy: { kind: "skip" } };

  const target = destination ?? sourceJobType;
  if (target !== sourceJobType && target !== "backtest") {
    throw new Error(
      `Cannot copy an exit policy from job type ${sourceJobType} into job type ${target}`,
    );
  }
  const resolved = resolveSourcePolicy(sourceJobType, sourceConfig, block);
  const policy = classify(resolved);
  // The container a copy's policy goes to in a destination of this job type.
  const path = [
    ...(target === sourceJobType ? block : []),
    target === "backtest" ? "exit_config" : "exit_param_overrides",
  ];

  if (target === sourceJobType) {
    if (policy.kind === "unrequested") {
      const container = asRecord(get(config, path));
      for (const key of EXIT_POLICY_KEYS) {
        const value = LEGACY_EXIT_POLICY[key];
        container[key] = target === "backtest" ? value : { fixed: value };
      }
      set(config, path, container);
    }
    return { config, policy };
  }

  const exitConfig = { ...asRecord(get(config, [...block, "exit_config"])) };
  for (const key of EXIT_POLICY_KEYS) {
    const entry = resolved[key];
    if (entry.state === "fixed") {
      exitConfig[key] = cloneDeep(entry.value);
    } else {
      delete exitConfig[key];
    }
  }
  if (policy.kind === "unrequested") Object.assign(exitConfig, LEGACY_EXIT_POLICY);
  set(config, path, exitConfig);
  return { config, policy };
};
